import React from 'react';
import PropTypes from 'prop-types';
import { css } from 'twin.macro';
import Popover from '../Popover/Popover';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import MessageBar from '../MessageBar/MessageBar';
import Select from '../Select/Select';
import FeatureFlagWrapper from '../FeatureFlagWrapper';
import { withRouter } from '../../libs/widgetUtilities';
import {
  defaultTabForWidget,
  filterVisibleEventInspectorTabs
} from '../../libs/tabFormatter';
import Button from '../Button/Button';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import { Body } from '../../styled components/Typography';
import { UIBar, UIBarGrowItem } from '../../styled components/UIBar';
import Sticky from '../../styled components/Sticky';

import {
  SettingInfoText,
  SettingListItem,
  SettingName,
  SettingsCategoryList,
  SettingLogoutButton,
  SettingCard,
  SettingIcon
} from './SettingsCenter.style';
import Checkbox from '../Checkbox/Checkbox';
import NavigationHistory from '../../libs/NavigationHistory';
import {
  HeaderActionsRight,
  Header,
  HeaderTitle,
  HeaderContent
} from '../PageHeaders/PageHeader.style';
import {
  capture,
  reset,
  PH_USER_LOGGED_OUT,
  PH_NOTIFICATION_SETTINGS_CHANGED
} from '../../libs/posthogAnalytics';

export class _SettingsCenter extends React.Component {
  constructor(props) {
    super(props);

    this.renderSettingsCenter = this.renderSettingsCenter.bind(this);
    this.logout = this.logout.bind(this);
    this.renderEventWidgetSettings = this.renderEventWidgetSettings.bind(this);
    this.renderNotificationSettings = this.renderNotificationSettings.bind(this);
  }

  logout() {
    const { logoutOfApp, redirectToYesplanLogin } = this.props;
    capture(PH_USER_LOGGED_OUT);
    reset();
    logoutOfApp(() => redirectToYesplanLogin(true));
  }

  renderCloseButton() {
    const { navigationHistory } = this.props;
    return (
      <Button
        contentType="icon"
        size="medium"
        icon="close"
        onClick={(e) => {
          e.preventDefault();
          navigationHistory.goBack();
        }}
        dataAttributes={{
          'data-test-button': 'closePopover'
        }}
      />
    );
  }

  renderSettingsCenterHeader() {
    return (
      <Sticky $zIndex={100} $top={0}>
        <Header>
          <HeaderContent>
            <HeaderTitle>{i18n('Settings')}</HeaderTitle>
            <HeaderActionsRight>{this.renderCloseButton()}</HeaderActionsRight>
          </HeaderContent>
        </Header>
      </Sticky>
    );
  }

  static renderSettingsHeader(name, svgIcon, action) {
    return (
      <UIBar tw="mb-4">
        <SettingIcon icon={svgIcon} />
        <Body $color="black">{name}</Body>
        <UIBarGrowItem />
        {action !== null && action}
      </UIBar>
    );
  }

  static renderSetting(name, value) {
    return (
      <SettingListItem key={name}>
        <SettingName as="p">{name}</SettingName>
        <Body as="p" $color="black">
          {value}
        </Body>
      </SettingListItem>
    );
  }

  static renderSettings(name, svgIcon, action, settingsList) {
    return (
      <div key={name}>
        {this.renderSettingsHeader(name, svgIcon, action)}
        <SettingCard data-test-type="settingsItem">
          <SettingsCategoryList>{settingsList}</SettingsCategoryList>
        </SettingCard>
      </div>
    );
  }

  renderProfileSettings() {
    const { user } = this.props;
    const { name, email, username, host } = user;
    const settings = [];
    let hostForSetting = host;
    if (name && name.length > 0) {
      settings.push({
        name: i18n('Name'),
        value: name
      });
    }
    if (email && email.length > 0) {
      settings.push({
        name: i18n('Email'),
        value: email
      });
    }
    if (username && username.length > 0) {
      settings.push({
        name: i18n('Username'),
        value: username
      });
    }
    if (host) {
      if (host === 'localhost:8080' || host === 'testklant.yesplan.app')
        hostForSetting = `http://${host}`;
      else hostForSetting = `https://${host}`;
      settings.push({
        name: i18n('Installation'),
        value: hostForSetting
      });
    }

    return this.constructor.renderSettings(
      i18n('Profile'),
      'user',
      <SettingLogoutButton onClick={this.logout} size="small" color="primary">
        {i18n('Sign Out')}
      </SettingLogoutButton>,
      <>
        {settings.map((setting) =>
          this.constructor.renderSetting(setting.name, setting.value)
        )}
      </>
    );
  }

  renderNotificationSettings() {
    const { user, setUserPreferenceRosterNotificationEMails } = this.props;
    const hasEmailAddress = user.email && user.email.length > 0;
    return this.constructor.renderSettings(
      i18n('Notifications'),
      'notification-empty',
      null,
      <div>
        <UIBar tw="mb-4">
          <Body as="span" $color="black">
            {i18n('Receive by email')}
          </Body>
          <UIBarGrowItem />
          <Checkbox
            variant="switch"
            disabled={!hasEmailAddress}
            defaultChecked={hasEmailAddress && user.preferences.rosternotifications.email}
            onChange={() => {
              setUserPreferenceRosterNotificationEMails(
                !user.preferences.rosternotifications.email
              );
              capture(PH_NOTIFICATION_SETTINGS_CHANGED, { email: !user.preferences.rosternotifications.email });
            }}
          />
        </UIBar>
        <SettingInfoText>
          {!hasEmailAddress &&
            user.hasBackOfficeAccess &&
            i18n('No address set. You can enter it in your Yesplan User Settings.')}
          {!hasEmailAddress &&
            !user.hasBackOfficeAccess &&
            i18n('No address set. Please ask your Yesplan administrator to add it.')}
        </SettingInfoText>
      </div>
    );
  }

  renderEventWidgetSettings() {
    const { user, visibletabs, setUserPreferenceDefaultEventInspectorTabApp } =
      this.props;
    const { defaultEventInspectorTabApp } = user.preferences;
    let visibleEventTabs = filterVisibleEventInspectorTabs(visibletabs.eventtabs) || [];
    const defaultIsSelectable =
      defaultEventInspectorTabApp &&
      visibleEventTabs.find((tab) => tab.tabid === defaultEventInspectorTabApp.tabid);
    if (!defaultIsSelectable && defaultEventInspectorTabApp !== null)
      visibleEventTabs = [defaultEventInspectorTabApp, ...visibleEventTabs];
    const actualDefaultEventInspectorTab = defaultTabForWidget(
      defaultEventInspectorTabApp,
      visibleEventTabs
    );
    return this.constructor.renderSettings(
      i18n('Events'),
      'production',
      null,
      <SettingListItem key="eventdetailwidget-tab">
        <div tw="mb-8">
          <SettingName>{i18n('Always open events on tab')}</SettingName>
          {!defaultIsSelectable && visibleEventTabs.length === 0 && (
            <SettingInfoText>
              {i18n('Tab {tabName} is no longer visible', {
                tabName: defaultEventInspectorTabApp
                  ? defaultEventInspectorTabApp.name
                  : ''
              })}
            </SettingInfoText>
          )}
        </div>
        {!defaultIsSelectable &&
          visibleEventTabs.length > 0 &&
          defaultEventInspectorTabApp !== null && (
            <MessageBar variant="error" size="small" tw="inline-block mb-8">
              {i18n('The “{tabName}” tab is no longer visible to you.', {
                tabName: defaultEventInspectorTabApp.name
              })}
            </MessageBar>
          )}
        {!defaultIsSelectable && visibleEventTabs.length === 0 && (
          <MessageBar variant="error" size="small" tw="inline-block mb-8">
            {i18n('There are no tabs visible to you.')}
          </MessageBar>
        )}
        {actualDefaultEventInspectorTab && (
          <Body as="div" $color="black">
            <Select
              name="eventtabs-select"
              options={visibleEventTabs.map((tab) => ({
                text: tab.name,
                value: tab.tabid,
                disabled:
                  tab.tabid === actualDefaultEventInspectorTab.tabid
                    ? !defaultIsSelectable
                    : false
              }))}
              selected={{
                text: actualDefaultEventInspectorTab.name,
                value: actualDefaultEventInspectorTab.tabid
              }}
              onChange={(value) => {
                const tab = visibleEventTabs.find((tab) => tab.tabid === value.value);
                setUserPreferenceDefaultEventInspectorTabApp(tab);
              }}
              valid={defaultIsSelectable !== undefined}
            />
          </Body>
        )}
      </SettingListItem>
    );
  }

  renderSettingsCenter() {
    const { navs } = this.props;
    return (
      <ErrorBoundary>
        {this.renderSettingsCenterHeader()}
        <div>
          <ContainerWrapper $paddingY={16}>
            {this.renderProfileSettings()}
            {/* Do not render the settings for notifications if the teamplanner is not actived for this installation */}
            <FeatureFlagWrapper
              feature={'rosterWidget'}
              isFeatureActive={() => navs.find((widget) => widget.id === 'roster')}
              renderActive={this.renderNotificationSettings}
            />
            {this.renderEventWidgetSettings()}
          </ContainerWrapper>
        </div>
      </ErrorBoundary>
    );
  }

  render() {
    const { location, history, navigationHistory } = this.props;
    return (
      <Popover
        renderContent={this.renderSettingsCenter}
        location={location}
        history={history}
        navigationHistory={navigationHistory}
      />
    );
  }
}

_SettingsCenter.propTypes = {
  redirectToYesplanLogin: PropTypes.func.isRequired,
  logoutOfApp: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    host: PropTypes.string,
    username: PropTypes.string,
    hasBackOfficeAccess: PropTypes.bool.isRequired,
    preferences: PropTypes.shape({
      rosternotifications: PropTypes.shape({
        email: PropTypes.bool.isRequired
      }).isRequired,
      defaultEventInspectorTabApp: PropTypes.shape({
        tabid: PropTypes.string,
        name: PropTypes.string
      })
    }).isRequired
  }).isRequired,
  visibletabs: PropTypes.shape({
    eventtabs: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  setUserPreferenceRosterNotificationEMails: PropTypes.func.isRequired,
  setUserPreferenceDefaultEventInspectorTabApp: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  location: PropTypes.shape({}).isRequired,
  navs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string
    })
  )
};

export default withRouter(_SettingsCenter);
